import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, message } from 'antd';
import MarketingLayout from './MarketingLayout';
import { getPublicChannelItems, submitPublicContactForm } from '../../services/content';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';

type ContactFormValues = {
  name: string;
  email: string;
  company?: string;
  country?: string;
  phone?: string;
  subject: string;
  message: string;
};

type ContactQrItem = {
  id: number;
  title: string;
  image: string;
  summary: string;
};

const pickFirstImageFromBody = (html: string): string => {
  const raw = String(html || '').trim();
  if (!raw) return '';
  const matched = raw.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/i);
  return matched?.[1] ? String(matched[1]).trim() : '';
};

const ContactPage: React.FC = () => {
  useDocumentMeta({
    title: '联系我们',
    description: '安飞供应链联系方式，欢迎跨境电商卖家和贸易客户就海外仓、转运、物流等业务需求与我们的团队沟通。',
  });

  const [form] = Form.useForm<ContactFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [contactQrRows, setContactQrRows] = useState<ContactQrItem[]>([]);
  const [previewQr, setPreviewQr] = useState<ContactQrItem | null>(null);

  const CONTACT_INFO_TEXT = [
    '公司名称：安飞供应链（AnFei Supply Chain）',
    '官网：www.anfeihub.com',
    '邮箱：contact@anfeihub.com',
    '电话：+1 857-333-5300',
    '地址：Nashua NH 03060 USA',
    '微信号：anfeihub',
    'WhatsApp：+1(857)333-5300',
    'QQ：553849761',
  ].join('\n');

  const handleCopyContactInfo = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_INFO_TEXT);
      message.success('联系信息已复制');
    } catch {
      message.error('复制失败，请手动复制');
    }
  };

  useEffect(() => {
    const loadContactQrs = async () => {
      try {
        const data = await getPublicChannelItems('home_contact_qrcodes');
        setContactQrRows(
          (data?.results || [])
            .map((item) => ({
              id: item.id,
              title: item.title || '联系方式二维码',
              image: (item.image_url || '').trim() || pickFirstImageFromBody(item.body || ''),
              summary: item.summary || '',
            }))
            .filter((item) => item.image)
        );
      } catch {
        setContactQrRows([]);
      }
    };
    loadContactQrs();
  }, []);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await submitPublicContactForm({
        name: values.name.trim(),
        email: values.email.trim(),
        company: (values.company || '').trim(),
        country: (values.country || '').trim(),
        phone: (values.phone || '').trim(),
        subject: values.subject.trim(),
        message: values.message.trim(),
      });
      Modal.success({
        title: '提交成功',
        content: res?.detail || '提交成功，我们会尽快联系您',
        okText: '确定',
      });
      form.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return;
      const detail = error?.response?.data?.detail;
      Modal.error({
        title: '提交失败',
        content: detail || '提交失败，请稍后重试',
        okText: '确定',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 py-14">
        <h1 className="text-3xl font-bold text-gray-900">联系我们</h1>
        <p className="mt-3 max-w-4xl text-sm leading-7 text-gray-600">
          安飞供应链团队欢迎您,请通过下方联系方式或提交右侧表单联系我们,谢谢!
        </p>
      </section>

      <section className="mx-auto grid max-w-6xl gap-4 px-6 pb-20 md:grid-cols-2">
        <div className="h-full rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="space-y-2 text-sm leading-7 text-gray-600">
            <div className="flex items-center justify-between gap-3">
              <p>公司名称：安飞供应链（AnFei Supply Chain）</p>
              <Button size="small" onClick={handleCopyContactInfo}>
                复制信息
              </Button>
            </div>
            <p>官网：www.anfeihub.com</p>
            <p>邮箱：contact@anfeihub.com</p>
            <p>电话：+1 857-333-5300</p>
            <p>地址：Nashua NH 03060 USA</p>
            <p>微信号：anfeihub</p>
            <p>WhatsApp：+1(857)333-5300</p>
            <p>QQ：553849761</p>
          </div>
          {contactQrRows.length ? (
            <div className="mt-6 border-t border-gray-100 pt-4">
              <div className="flex flex-wrap gap-4">
                {contactQrRows.map((qr) => (
                  <button
                    key={qr.id}
                    type="button"
                    className="flex w-[110px] flex-col items-center gap-2 rounded-lg border border-gray-100 bg-white p-2 text-center hover:border-[#bcdcff] hover:shadow-sm"
                    onClick={() => setPreviewQr(qr)}
                    title={qr.title}
                  >
                    <img
                      src={qr.image}
                      alt={qr.title}
                      className="h-24 w-24 rounded border border-gray-100 bg-white object-contain p-1"
                    />
                    <span className="line-clamp-1 text-xs text-gray-700">{qr.title}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="rounded-xl border border-[#d9eaff] bg-white p-6 shadow-sm">
          <Form<ContactFormValues> form={form} layout="vertical">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                <Input placeholder="请输入姓名" maxLength={80} />
              </Form.Item>
              <Form.Item
                name="email"
                label="邮箱"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '邮箱格式不正确' },
                ]}
              >
                <Input placeholder="请输入邮箱" maxLength={254} />
              </Form.Item>
              <Form.Item name="company" label="公司名称(可选)">
                <Input placeholder="请输入公司名称" maxLength={120} />
              </Form.Item>
              <Form.Item name="country" label="所在国家(可选)">
                <Input placeholder="请输入所在国家" maxLength={80} />
              </Form.Item>
              <Form.Item name="phone" label="电话(可选,请输入完整国家码)" className="md:col-span-2">
                <Input placeholder="例如 +1 857-333-5300" maxLength={40} />
              </Form.Item>
              <Form.Item
                name="subject"
                label="主题"
                className="md:col-span-2"
                rules={[{ required: true, message: '请输入咨询主题' }]}
              >
                <Input placeholder="请输入咨询主题" maxLength={160} />
              </Form.Item>
              <Form.Item
                name="message"
                label="留言内容"
                className="md:col-span-2"
                rules={[
                  { required: true, message: '请输入留言内容' },
                  { min: 10, message: '留言内容至少10个字符' },
                ]}
              >
                <Input.TextArea rows={6} placeholder="请填写详细需求，我们会尽快联系您。" maxLength={5000} showCount />
              </Form.Item>
            </div>
            <div className="mt-2 flex justify-end">
              <Button type="primary" loading={submitting} onClick={handleSubmit}>
                提交咨询
              </Button>
            </div>
          </Form>
        </div>
      </section>
      {previewQr ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4" onClick={() => setPreviewQr(null)}>
          <div className="w-full max-w-[560px] rounded-xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-base font-semibold text-gray-900">{previewQr.title}</h4>
              <button
                type="button"
                className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => setPreviewQr(null)}
              >
                关闭
              </button>
            </div>
            <img
              src={previewQr.image}
              alt={previewQr.title}
              className="mx-auto h-[460px] w-[460px] max-w-full rounded border border-gray-100 bg-white object-contain p-2"
            />
            {previewQr.summary ? <p className="mt-3 text-sm text-gray-600">{previewQr.summary}</p> : null}
          </div>
        </div>
      ) : null}
    </MarketingLayout>
  );
};

export default ContactPage;
